import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import {
  AdminEmailOption,
  GopherBuddyCustomerSettings,
  isValidEmail,
  OrgUnitOption,
} from '@cdw-ae/common-data';
import { CdwAutocompleteModule } from '@cdw-ae/common-ui-components-autocomplete';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import {
  CdwConfirmationDialogComponent,
  CdwConfirmationDialogData,
} from '@cdw-ae/common-ui-components-dialog';
import { CdwFilteredTreePickerModule } from '@cdw-ae/common-ui-components-filtered-tree-picker';
import { CommonUiComponentsNumberInputComponent } from '@cdw-ae/common-ui-components-number-input';
import { CdwRadioButtonModule } from '@cdw-ae/common-ui-components-radio-button';
import { CdwSlideToggleModule } from '@cdw-ae/common-ui-components-slide-toggle';
import { CommonUiComponentsSpinnerComponent } from '@cdw-ae/common-ui-components-spinner';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { selectUserEmail } from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { environment } from '../../../../../../../environments/environment';
import { APP_NAME } from '../../../../../../common/constants/app.constants';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { RootState } from '../../../../../../common/interfaces/rootState.interface';
import { GopherBuddySettingsService } from './gopher-buddy-settings.service';

@Component({
  selector: 'gopher-buddy-setup',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatIconModule,
    ReactiveFormsModule,
    CdwRadioButtonModule,
    CdwAutocompleteModule,
    CdwButtonModule,
    CdwCheckboxModule,
    CdwSlideToggleModule,
    CommonUiComponentsTextInputModule,
    CommonUiComponentsNumberInputComponent,
    CdwFilteredTreePickerModule,
    CommonUiComponentsSpinnerComponent,
  ],
  templateUrl: './gopher-buddy-setup.component.html',
  styleUrl: './gopher-buddy-setup.component.scss',
})
export class GopherBuddySetupComponent implements OnInit {
  @Input() ouTreeData: TreeNode[] = [];

  private readonly store = inject(Store<RootState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly gopherBuddySettingsService = inject(
    GopherBuddySettingsService
  );

  public gopherBuddySubTabIndex = 0;
  public isSaving = false;
  public showEmailValidationError = false;
  public adminEmailTypedTextError = '';
  public adminEmailHasTypedText = false;
  public gopherBuddyForm!: FormGroup;
  public chromeUpdateNotificationForm!: FormGroup;
  public deviceInfoPanelForm!: FormGroup;

  public adminEmailOptions: AdminEmailOption[] = [];
  private currentSettings: GopherBuddyCustomerSettings | null = null;

  public currentUserEmail = '';
  public filteredAdminEmailOptions$ = new BehaviorSubject<AdminEmailOption[]>(
    []
  );

  public readonly tertiaryBlueCta: string = TailwindColors.tertiaryBlueCta;
  public readonly toggleTrackBg: string =
    TailwindColors.tertiaryBlueToggleTrackBg;
  public readonly primaryWhite: string = TailwindColors.primaryWhite;
  public readonly primaryBlack: string = TailwindColors.primaryBlack;
  public readonly appName = APP_NAME;
  public readonly gopherBuddySetupArticleUrl =
    environment.urls.support.articles.gopherBuddySetup;

  /**
   * Update filtered admin email options based on selected admin type
   * "select" shows super admins, "manual" shows delegated admins
   */
  private updateFilteredAdminEmailOptions(): void {
    const adminType = this.gopherBuddyForm?.get('adminType')?.value;
    const filterType = adminType === 'select' ? 'super' : 'delegated';
    const filtered = this.adminEmailOptions.filter(
      (opt) => opt.value.type === filterType
    );
    this.filteredAdminEmailOptions$.next(filtered);
  }

  /**
   * Validates that a comma-separated string contains only valid email addresses.
   * Empty string is allowed (no emails required).
   */
  static commaEmailsValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) return null;

    if (/^,|,,|,$/.test(value)) {
      return { formatError: { value: 'Remove extra or misplaced commas' } };
    }

    const emails = value.split(',').map((e: string) => e.trim());
    const invalidEmails = emails.filter(
      (email: string) => !isValidEmail(email)
    );

    return invalidEmails.length
      ? { invalidEmails: { value: invalidEmails } }
      : null;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Initialize admin settings form
    this.gopherBuddyForm = this.fb.group({
      adminType: ['select'], // default to super administrator
      adminEmail: [''],
    });

    // Initialize Chrome update notification form
    this.chromeUpdateNotificationForm = this.fb.group({
      allowNotifications: [false], // Yes, allow
      displayNotification: [false],
      majorVersionsThreshold: [],
      restrictMajorUpdates: [false],
      restrictMajorVersions: [],
      enableMinorRestriction: [false],
      minorVersionsThreshold: [],
      restrictMinorUpdates: [false],
      restrictMinorVersions: [],
      reminderEmails: ['', [GopherBuddySetupComponent.commaEmailsValidator]],
    });

    // Initialize device info panel form
    this.deviceInfoPanelForm = this.fb.group({
      allowDeviceInfoAccess: [false], // No, don't allow
      basicDeviceInfoOus: [[]],
      fullDeviceInfoOus: [[]],
    });

    // Update filtered options on init (after all forms are initialized)
    this.updateFilteredAdminEmailOptions();

    // Clear admin email and update filtered options when admin type changes
    this.gopherBuddyForm
      .get('adminType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gopherBuddyForm.patchValue({ adminEmail: null });
        this.updateFilteredAdminEmailOptions();
      });

    // Clear admin email validation when a valid dropdown selection is made
    this.gopherBuddyForm
      .get('adminEmail')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) {
          this.adminEmailTypedTextError = '';
          this.adminEmailHasTypedText = false;
        }
      });

    // Subscribe to current user email from store
    this.store
      .select(selectUserEmail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userEmail) => {
        this.currentUserEmail = userEmail;
      });

    // Load admin list and settings, then apply when both are ready
    combineLatest([
      this.gopherBuddySettingsService.getAdmins(),
      this.gopherBuddySettingsService.getCustomerSettings(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([admins, settings]) => {
          this.adminEmailOptions = admins;
          this.currentSettings = settings;
          this.updateFilteredAdminEmailOptions();
          this.applySettings(settings);
        },
        error: () => {
          this.snackbarService.showError(
            'Failed to load settings. Please refresh the page.'
          );
        },
      });
  }

  /**
   * Apply settings from API response to all forms
   */
  private applySettings(settings: GopherBuddyCustomerSettings): void {
    const opts = settings.settings;

    // Update admin settings form
    const emailToFind =
      opts.adminType === 'select'
        ? opts.selectedAdminEmail
        : opts.manualAdminEmail;
    const matchingOption = this.adminEmailOptions.find(
      (opt) => opt.value.email === emailToFind
    );

    this.gopherBuddyForm.patchValue({
      adminType: opts.adminType,
      adminEmail: matchingOption ?? null,
    });

    // Update Chrome update notification form
    this.chromeUpdateNotificationForm.patchValue({
      allowNotifications: opts.allowChromeOsUpdateReminders ?? false,
      displayNotification: opts.allowChromeOsUpdateReminders ?? false,
      majorVersionsThreshold: opts.popupThreshold ?? null,
      restrictMajorUpdates: opts.restrictChromeOsVersionUpdates ?? false,
      restrictMajorVersions: opts.chromeOsVersionMax ?? null,
      enableMinorRestriction: opts.restrictChromeOsMinorVersion ?? false,
      minorVersionsThreshold: opts.minorVersionThreshold ?? null,
      restrictMinorUpdates: opts.restrictChromeOsMinorVersion ?? false,
      restrictMinorVersions: opts.chromeOsMinorVersionMax ?? null,
      reminderEmails: opts.reminderEmails?.join(', ') ?? '',
    });

    // Extract OUs by access type
    const basicOus = this.extractOusByAccessType(
      opts.deviceInfo?.ous ?? [],
      'basic'
    );
    const fullOus = this.extractOusByAccessType(
      opts.deviceInfo?.ous ?? [],
      'full'
    );

    // Update device info panel form (including tree picker values)
    this.deviceInfoPanelForm.patchValue({
      allowDeviceInfoAccess: opts.deviceInfo?.allow ?? false,
      basicDeviceInfoOus: basicOus,
      fullDeviceInfoOus: fullOus,
    });
  }

  /**
   * Extract OU values by access type from API response
   */
  private extractOusByAccessType(
    ous: OrgUnitOption[],
    accessType: string
  ): string[] {
    return ous
      .filter((ou) => ou.accessType === accessType)
      .map((ou) => ou.value);
  }

  /**
   * Find a node in the tree by its value
   */
  private findNodeByValue(nodes: TreeNode[], value: string): TreeNode | null {
    for (const node of nodes) {
      if (node.value === value) {
        return node;
      }
      if (node.children?.length) {
        const found = this.findNodeByValue(node.children, value);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Convert tree picker string values to OrgUnitOption array for API
   */
  private convertToOrgUnitOptions(
    values: string[],
    accessType: 'basic' | 'full'
  ): OrgUnitOption[] {
    return values.map((value) => {
      const node = this.findNodeByValue(this.ouTreeData, value);
      const hasChildren = !!node?.children?.length;

      return {
        accessType,
        includeSubOus: hasChildren,
        orgUnitId: '000000000000000',
        value,
      };
    });
  }

  public onGopherBuddySubTabChange(tabIndex: number): void {
    this.gopherBuddySubTabIndex = tabIndex;
  }

  public onAdminEmailTextChanged(text: string): void {
    const trimmed = text?.trim() ?? '';
    this.adminEmailHasTypedText = trimmed !== '';

    if (!trimmed) {
      this.adminEmailTypedTextError = '';
      return;
    }
    this.adminEmailTypedTextError = isValidEmail(trimmed)
      ? ''
      : 'Please enter a valid email address or select from the list';
  }

  public saveAdminSettings(): void {
    this.saveSettings();
  }

  public saveConfigurationPreferences(): void {
    this.saveSettings();
  }

  /**
   * Build settings payload from all forms and save to API
   */
  private saveSettings(): void {
    if (this.isSaving || this.adminEmailTypedTextError || this.adminEmailHasTypedText) {
      return;
    }

    if (!this.currentSettings) {
      this.snackbarService.showError(
        'Settings could not be loaded. Please refresh the page.'
      );
      return;
    }

    if (this.chromeUpdateNotificationForm.get('reminderEmails')?.invalid) {
      this.showEmailValidationError = true;
      return;
    }
    this.showEmailValidationError = false;
    this.isSaving = true;

    const adminFormValue = this.gopherBuddyForm.value;
    const chromeFormValue = this.chromeUpdateNotificationForm.value;
    const deviceFormValue = this.deviceInfoPanelForm.value;

    // adminEmail form value can be AdminEmailOption (from patchValue) or AdminEmailValue (from user selection)
    const adminEmailValue = adminFormValue.adminEmail?.value?.email
      ? adminFormValue.adminEmail.value
      : adminFormValue.adminEmail;
    const selectedEmail = adminEmailValue?.email ?? '';

    // Parse reminder emails from comma-separated string
    const reminderEmails = chromeFormValue.reminderEmails
      ? chromeFormValue.reminderEmails
          .split(',')
          .map((e: string) => e.trim())
          .filter((e: string) => e)
      : [];

    // Build OUs array from basic and full selections
    const basicOus = this.convertToOrgUnitOptions(
      deviceFormValue.basicDeviceInfoOus ?? [],
      'basic'
    );
    const fullOus = this.convertToOrgUnitOptions(
      deviceFormValue.fullDeviceInfoOus ?? [],
      'full'
    );

    const settings: GopherBuddyCustomerSettings = {
      customerId: this.currentSettings.customerId,
      licenseTier: this.currentSettings.licenseTier,
      emailTrigger: this.currentSettings.emailTrigger,
      settings: {
        adminType: adminFormValue.adminType,
        selectedAdminEmail:
          adminFormValue.adminType === 'select' ? selectedEmail : '',
        manualAdminEmail:
          adminFormValue.adminType === 'manual' ? selectedEmail : '',
        allowChromeOsUpdateReminders:
          chromeFormValue.allowNotifications ?? false,
        popupThreshold: chromeFormValue.majorVersionsThreshold ?? 0,
        restrictChromeOsVersionUpdates:
          chromeFormValue.restrictMajorUpdates ?? false,
        chromeOsVersionMax: chromeFormValue.restrictMajorVersions ?? 99999,
        restrictChromeOsMinorVersion:
          chromeFormValue.enableMinorRestriction ?? false,
        minorVersionThreshold: chromeFormValue.minorVersionsThreshold ?? 0,
        chromeOsMinorVersionMax: chromeFormValue.restrictMinorVersions ?? 99999,
        reminderEmails,
        deviceInfo: {
          allow: deviceFormValue.allowDeviceInfoAccess ?? false,
          ous: [...basicOus, ...fullOus],
        },
        extensionIntervalInSeconds:
          this.currentSettings.settings?.extensionIntervalInSeconds ?? 300,
        firstTimeInstall:
          this.currentSettings.settings?.firstTimeInstall ?? false,
      },
    };

    this.gopherBuddySettingsService
      .setCustomerSettings(settings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.isSaving = false;
          if (result.success) {
            this.currentSettings = settings;
            this.snackbarService.showMessage('Configuration preferences saved successfully');
          } else {
            this.snackbarService.showError('Failed to save configuration preferences');
          }
        },
        error: () => {
          this.isSaving = false;
          this.snackbarService.showError('Failed to save configuration preferences');
        },
      });
  }

  public requestPermanentDeletion(): void {
    const dialogData: CdwConfirmationDialogData = {
      title: 'Additional Confirmation Required',
      message: '',
      htmlMessage:
        '<span class="flex flex-col gap-2">Continuing will delete all of your data from our systems. This action is permanent and cannot be undone.<span>Type <strong>DELETE</strong> to <strong>CONTINUE</strong>.</span></span>',
      type: 'error',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      requireInputConfirmation: true,
      inputLabel: 'enter "DELETE"',
      requiredText: 'DELETE',
      confirmButtonColor: 'warn',
      hideIcon: true,
      messageFontWeight: 400,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '675px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        // TODO: Implement deletion logic
        // console.log('Deletion confirmed');
      }
    });
  }

  public resetGBSettings(): void {
    const dialogData: CdwConfirmationDialogData = {
      title: 'Additional Confirmation Required',
      message: '',
      htmlMessage:
        '<span class="flex flex-col gap-2">Continuing will reset all Gopher Buddy settings for your domain(s).<span>Captured Gopher Buddy session data will NOT be lost.</span><span>Type <strong>RESET</strong> in the field below and then click CONTINUE to confirm this action.</span></span>',
      type: 'warning',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      requireInputConfirmation: true,
      inputLabel: 'enter "RESET"',
      requiredText: 'RESET',
      hideIcon: true,
      messageFontWeight: 400,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '675px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        // TODO: Implement reset logic
        // console.log('Reset confirmed');
      }
    });
  }
}
